import React, { useEffect, useState, useMemo } from "react";

/**
 * Cinematic artist slideshow.
 * - Each image displays for 5s
 * - ~1.4s cross-fade between images
 * - Ken Burns effect (scale 1 → 1.05) during display
 * - Preloads all images to prevent flash
 * - Respects prefers-reduced-motion
 * - Text/UI overlaid separately and remains completely static
 */
const ARTISTS = [
    {
        name: "Diljit Dosanjh",
        src: "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/xm1wy5g2_Frame%20593.png",
        mobileSrc:
            "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/xm1wy5g2_Frame%20593.png",
        position: "50% 30%",
        mobilePosition: "55% 15%",
    },
    {
        name: "Shan Vincent de Paul",
        src: "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/nicmzohf_Frame%20595.png",
        mobileSrc:
            "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/cenjunrp_ChatGPT%20Image%20Sep%2030%2C%202026%2C%2004_57_40%20PM%201.png",
        position: "65% 40%",
        mobilePosition: "55% 10%",
    },
    {
        name: "Nav47",
        src: "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/u948i4a9_Frame%20607.png",
        mobileSrc:
            "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/oea2pgga_ChatGPT%20Image%20Sep%2030%2C%202026%2C%2004_56_52%20PM%201.png",
        position: "75% 40%",
        mobilePosition: "55% 15%",
    },
    {
        name: "Arivu",
        src: "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/6eor70rr_Frame%20608.png",
        mobileSrc:
            "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/2r015b71_-1x-1%201-1.png",
        position: "70% 40%",
        mobilePosition: "55% 10%",
    },
    {
        name: "Dhee",
        src: "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/ofhj84t0_Frame%20596.png",
        mobileSrc:
            "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/ci3zfa0q_ChatGPT%20Image%20Oct%203%2C%202026%2C%2006_39_45%20PM%201.png",
        position: "60% 35%",
        mobilePosition: "55% 15%",
    },
    {
        name: "Roy Woods",
        src: "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/sosg6don_desktop.png",
        mobileSrc:
            "https://customer-assets-eiarnc6j.emergentagent.net/job_music-tech-hub-1/artifacts/ue5owo5a_phone.png",
        position: "75% 35%",
        mobilePosition: "50% 20%",
    },
];

const DISPLAY_MS = 5000;
const FADE_MS = 1400;

export const ArtistSlideshow = () => {
    const [active, setActive] = useState(0);
    const [reducedMotion, setReducedMotion] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Preload every image once (desktop + mobile variants) so crossfade is instant.
    useEffect(() => {
        ARTISTS.forEach((a) => {
            const img1 = new Image();
            img1.src = a.src;
            if (a.mobileSrc) {
                const img2 = new Image();
                img2.src = a.mobileSrc;
            }
        });
    }, []);

    useEffect(() => {
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const mobileMq = window.matchMedia("(max-width: 767px)");
        const onChange = () => setReducedMotion(mq.matches);
        const onMobile = () => setIsMobile(mobileMq.matches);
        onChange();
        onMobile();
        mq.addEventListener("change", onChange);
        mobileMq.addEventListener("change", onMobile);
        return () => {
            mq.removeEventListener("change", onChange);
            mobileMq.removeEventListener("change", onMobile);
        };
    }, []);

    useEffect(() => {
        if (reducedMotion) return; // freeze on first image
        const t = setInterval(
            () => setActive((i) => (i + 1) % ARTISTS.length),
            DISPLAY_MS,
        );
        return () => clearInterval(t);
    }, [reducedMotion]);

    // Total in-slot duration so Ken Burns finishes just as fade-out starts.
    const kenBurnsMs = useMemo(() => DISPLAY_MS + FADE_MS, []);

    return (
        <div
            className="absolute inset-0 overflow-hidden"
            data-testid="artist-slideshow"
            aria-hidden="true"
        >
            {ARTISTS.map((a, i) => {
                const isActive = i === active;
                return (
                    <div
                        key={a.src}
                        className="absolute inset-0"
                        style={{
                            opacity: isActive ? 1 : 0,
                            transition: `opacity ${FADE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                            willChange: "opacity",
                            backgroundColor: isMobile && a.mobileBg ? a.mobileBg : undefined,
                        }}
                    >
                        <img
                            src={isMobile && a.mobileSrc ? a.mobileSrc : a.src}
                            alt={`Featured artist — ${a.name}`}
                            className="w-full h-full"
                            style={{
                                objectFit:
                                    isMobile && a.mobileFit
                                        ? a.mobileFit
                                        : "cover",
                                objectPosition: isMobile
                                    ? a.mobilePosition
                                    : a.position,
                                transform:
                                    reducedMotion || isMobile
                                        ? `scale(${isMobile && a.mobileScale ? a.mobileScale : 1})`
                                        : isActive
                                        ? "scale(1.055)"
                                        : "scale(1)",
                                transformOrigin: "center top",
                                transition:
                                    reducedMotion || isMobile
                                        ? "none"
                                        : `transform ${kenBurnsMs}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                                willChange: isMobile ? "auto" : "transform",
                            }}
                        />
                    </div>
                );
            })}
        </div>
    );
};
